import type { Agent, AgentListParams } from '@/api/agent/types';
import { defineStore } from 'pinia';
import { agentApi } from '@/api';
import { useUserStore } from './user';

// Re-export the Agent type
export type { Agent };

export const useAgentStore = defineStore('agent', () => {
  // 当前选择的智能体
  const currentAgent = ref<Agent | null>(null);

  // 智能体列表
  const agents = ref<Agent[]>([]);

  // 加载状态
  const loading = ref(false);

  // 并发去重：同一时间只允许一个列表请求在飞
  let fetchAgentsInFlight: Promise<void> | null = null;

  // 获取智能体列表
  const fetchAgents = async (params?: AgentListParams) => {
    // 已有请求在飞时，直接复用，避免初始化重复请求
    if (fetchAgentsInFlight)
      return fetchAgentsInFlight;

    fetchAgentsInFlight = (async () => {
      const userStore = useUserStore();
      // 如果没有 token，清空数据并直接返回
      if (!userStore.token) {
        agents.value = [];
        loading.value = false;
        return;
      }

      try {
        loading.value = true;
        const data = await agentApi.getAgentList(params);
        if (data && data.success) {
          // 处理不同的数据格式
          if (data.data && Array.isArray(data.data)) {
            // 直接数组格式
            agents.value = data.data as Agent[];
          }
          else if (data.data && data.data.list && Array.isArray(data.data.list)) {
            // 分页格式，包含list字段
            agents.value = data.data.list as Agent[];
          }
          else {
            console.warn('智能体列表数据格式异常:', data);
            agents.value = [];
          }
        }
      }
      catch (error) {
        console.error('获取智能体列表失败:', error);
      }
      finally {
        loading.value = false;
      }
    })();

    try {
      await fetchAgentsInFlight;
    }
    finally {
      fetchAgentsInFlight = null;
    }
  };

  // 根据ID获取智能体详情
  const fetchAgentById = async (id: string) => {
    try {
      const data = await agentApi.getAgentById(id);
      if (data && data.success) {
        return data.data;
      }
    }
    catch (error) {
      console.error('获取智能体详情失败:', error);
    }
    return null;
  };

  // 设置当前智能体
  const setCurrentAgent = (agent: Agent) => {
    currentAgent.value = agent;
    // 保存到本地存储
    localStorage.setItem('currentAgent', JSON.stringify(agent));
  };

  // 获取当前智能体
  const getCurrentAgent = () => {
    if (currentAgent.value) {
      return currentAgent.value;
    }

    // 从本地存储获取
    const stored = localStorage.getItem('currentAgent');
    if (stored) {
      try {
        const agent = JSON.parse(stored);
        currentAgent.value = agent;
        return agent;
      }
      catch (error) {
        console.error('解析智能体数据失败:', error);
      }
    }

    return null;
  };

  // 清除当前智能体
  const clearCurrentAgent = () => {
    currentAgent.value = null;
    localStorage.removeItem('currentAgent');
  };

  // 根据ID获取智能体
  const getAgentById = (id: string) => {
    return agents.value.find(agent => agent.id === id) || null;
  };

  return {
    currentAgent,
    agents,
    loading,
    fetchAgents,
    fetchAgentById,
    setCurrentAgent,
    getCurrentAgent,
    clearCurrentAgent,
    getAgentById,
  };
});
